import React, { useCallback, useContext, useEffect, useMemo } from "react";

import _ from "lodash";
import cx from 'classnames';


import { useNavigate, useParams } from "react-router-dom";

import { CollectionAsset, CollectionEditState } from "../../../../types/collections";
import { GridLayout } from "../../experiences/metro/grid";
import { animated, useSpring } from "@react-spring/web";
import { AssetLayoutProps, MenuState, SectionBarProps, SectionState } from "../../types";
import Slider from "../../../../components/slider";
import { Input } from "../../../../components/input";
import Reveal from "../../../../components/animation";
import Button from "../../../../components/button";

import styles from './content.module.scss';
import { CollectionContext } from "../context";
import { useCollectionsAdoption } from "src/redux/actions/adoption/collection";

interface CollectionContentProps {
  collection: CollectionEditState;
  sectionBar: React.FC<SectionBarProps>;
  layout: React.FC<AssetLayoutProps>;
  menuState: MenuState;
  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void;
}

const CollectionContent: React.FC<CollectionContentProps> = ({ collection, sectionBar, layout, menuState, onMenuChange }) => {
  const navigate = useNavigate();
  const { sectionId } = useParams();
  const collectionContext = useContext(CollectionContext);

  const sectionItems = useMemo(() => {
    if (!collection.id || !collection.sections) {
      return [];
    }

    const items = collection.sections.map((item, index) => {
      const data = {
        uid: item.uid,
        id: item.item?.id,
        index: index,
        name: item.item?.name,
        entities: item.item?.entities,
        loading: item.loading,
        loaded: item.loaded,
        active: Number(sectionId) === index,
      } as SectionState;

      return data;
    });

    return items;
  }, [collection, sectionId]);

  useEffect(() => {
    const activeIndex = parseInt(sectionId || '0');
    if (collection.id && activeIndex >= 0) {
      const section = sectionItems[activeIndex];
      if (section && !section.loaded && section.id) {
        collectionContext.fetchSection(section.id);
      }
    }
  }, [sectionId, collection.id, _.size(sectionItems)]);

  useEffect(() => {
    const activeIndex = parseInt(sectionId || '0');
    const sectionsCount = _.size(sectionItems);
    if(sectionsCount > 0 && activeIndex >= sectionsCount - 1){ 
      collectionContext.onSectionClick(sectionsCount - 1);
    } else if(sectionsCount == 0 && sectionId != null){
      //navigate(`/collections/${collection.id}`);  
    }
  }, [sectionId, _.size(sectionItems)]);

  const handleSectionClick = (index) => {
    collectionContext.onSectionClick(index);
  }

  const handleAddSectionFinish = () => {
    handleAddSectionClose();
    const index = _.size(sectionItems);
    collectionContext.onSectionClick(index);
  }

  const handleAddSectionClose = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: null
    });
  }

  const handleRemoveSection = (index) => {
    collectionContext.removeSection(index);
  }

  const handleRenameSection = (section: number, value: string) => {
    collectionContext.renameSection(section, value);
  }

  const handleRemoveAssetFromSection = (section: SectionState, asset: CollectionAsset) => {
    collectionContext.removeAsset(section.index, asset.id);
  }

  const handleRemoveAsset = (asset: CollectionAsset) => {
    const targetAssetId = _.get(asset, 'id');

    if (!targetAssetId) {
      return;
    }
    
    collectionContext.removeAsset(null, targetAssetId);
  }

  const handleAssetClick = (asset: CollectionAsset) => {
    handleAssetClickInSection(null, asset)
  }

  const handleAssetClickInSection = (section: SectionState | null, asset: CollectionAsset) => {
    const sectionIndex = section ? section.index : 0;
    let source = `collections/${collection.id}`;
    if(sectionIndex >= 0) {
      source = `${source}/sections/${sectionIndex}`;
    }

    const url = `/assets/${asset.asset.id}?source=${source}`; 
    navigate(url);
  }

  const handleAssetMove = (section: SectionState | null, from: number, to: number) => {
    if (section?.index !== undefined) {
      collectionContext.moveAsset(section.index, from, to);
    }else {
      collectionContext.moveAsset(null, from, to);
    }
  }

  const handleAssetMoveToSection = (source: SectionState, target: number, asset: CollectionAsset) => {
    collectionContext.moveAssetToSection(source.index, target, asset);
  }

  const handleSectionMove = (from: number, to: number) => {
    const section = sectionItems[from];
    if (!section) {
      return;
    }

    let newActiveIndex = -1;
    const activeSection = sectionItems.find(s => s.active)?.index || 0;
    if (to <= activeSection && from > activeSection) {
      newActiveIndex = activeSection + 1;
    } else if (from === activeSection) {
      newActiveIndex = to;
    } else if (from < activeSection && to >= activeSection) {
      newActiveIndex = activeSection - 1;
    }

    collectionContext.moveSection(from, to);

    if (newActiveIndex >= 0) {
      //navigate(`/collections/${collection.id}/sections/${newActiveIndex}`, { replace: true });
      collectionContext.onSectionClick(newActiveIndex);
    }
  }

  const setContainerRef = useCallback((ref: HTMLElement | null) => {
    if (ref) {
      ref.addEventListener('scroll', (e) => {
        ref.scrollLeft = 0; 
      });
    }
  }, []);

  return (
    <div className={styles.container} ref={setContainerRef}>
      {_.size(sectionItems) > 0 &&
        <>
          <div className={styles.sectionBar}>
            {React.createElement(sectionBar, {
              collection: collection,
              sections: sectionItems,
              selected: Number(sectionId),
              menuState: menuState,
              onMenuChange: onMenuChange,
              onSelectionChange: handleSectionClick,
              onRename: handleRenameSection,
              onRemove: handleRemoveSection,
              onMove: handleSectionMove,
              onDrop: () => { }
            })}
          </div>
          <div className={styles.sections}>
            {sectionItems.map((section, index) => {
              return (
                <SectionContainer key={index}
                  collection={collection}
                  section={section}
                  layout={layout}
                  menuState={menuState}
                  onMenuChange={onMenuChange}
                  onClick={handleAssetClickInSection}
                  onRemove={handleRemoveAssetFromSection}
                  onMove={handleAssetMove}
                  onMoveToSection={handleAssetMoveToSection}
                />
              );
            })}
          </div>
        </>
      }

      {_.size(sectionItems) === 0 &&
        <>{React.createElement(layout, {
          assets: collection.entities || [],
          loading: false,
          visible: true,
          collection: collection,
          menuState: menuState,
          onMenuChange: onMenuChange,
          onClick: handleAssetClick,
          onRemove: handleRemoveAsset,
          onMove: (from, to) => handleAssetMove(null, from, to),
          onMoveToSection: () => { }
        })}</>
      }

      <AddSectionDialog
        collection={collection.id!}
        show={menuState.secondaryAction === 'add-section'}
        onFinish={handleAddSectionFinish}
        onClose={handleAddSectionClose}
      />
    </div>
  );
}

interface SectionContainerProps {
  collection: CollectionEditState;
  section: SectionState;
  layout: typeof GridLayout,
  menuState: MenuState;

  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void;

  // eslint-disable-next-line no-unused-vars
  onRemove: (section: SectionState, asset: CollectionAsset) => void;

  // eslint-disable-next-line no-unused-vars
  onClick: (section: SectionState, asset: CollectionAsset) => void;

  // eslint-disable-next-line no-unused-vars
  onMove: (section: SectionState, from: number, to: number) => void;

  // eslint-disable-next-line no-unused-vars
  onMoveToSection: (source: SectionState, target: number, asset: CollectionAsset) => void;

}

const SectionContainer: React.FC<SectionContainerProps> = ({ collection, section, layout, menuState, onMenuChange, onClick, onRemove, onMove, onMoveToSection }) => {
  const [mounted, setMounted] = React.useState(true);
  const [hidden, setHidden] = React.useState(false);

  const [animation] = useSpring({
    opacity: section.active ? 1 : 0,
    transform: section.active ? 'translate3d(0,0,0)' : 'translate3d(300px, 0, 0)',
    onRest: () => {
      const unmount = !section.active && _.size(section.entities) > 1;
      if (unmount) {
        setMounted(!unmount);
      }

      setHidden(!section.active);
    }
  }, [section]);

  useEffect(() => {
    if (section.active) {
      setMounted(section.active);
    }

    if (section.active) {
      setHidden(!section.active);
    }
  }, [section.active]);

  const handleAssetRemove = (asset: CollectionAsset) => {
    onRemove(section, asset);
  }

  const handleAssetClick = (asset: CollectionAsset) => {
    onClick(section, asset);
  }

  return (
    <animated.div style={animation} className={cx(styles.sectionContainer, {
      [styles.active]: section.active,
      [styles.hidden]: hidden
    })}>
      <div className={styles.inner}>
        {mounted &&
          <>
            {React.createElement(layout, {
              assets: section.entities,
              loading: section.loading,
              collection: collection,
              section: section.index,
              visible: section.active,
              menuState: menuState,
              onMenuChange: onMenuChange,
              onClick: handleAssetClick,
              onRemove: handleAssetRemove,
              onMove: (from, to) => onMove(section, from, to),
              onMoveToSection: (target, asset) => onMoveToSection(section, target, asset)
            })}
          </>
        }
      </div>
    </animated.div>
  );
}

//Add Section
interface AddSectionDialogProps {
  collection: string;
  show: boolean;
  // eslint-disable-next-line no-unused-vars
  onFinish: (index: number) => void;
  onClose: () => void;
}

const AddSectionDialog: React.FC<AddSectionDialogProps> = ({ collection, show, onFinish, onClose }) => {
  const collectionContext = useContext(CollectionContext);
  const { trackCreateSectionScreenView } = useCollectionsAdoption();

  const [name, setName] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    if (show) {
      trackCreateSectionScreenView();
      
      setName('');
      setError(null);
    }
  }, [show]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setError(null);
  }

  const handleCancel = () => {
    onClose();
  }

  const validateForm = () => {
    if (_.isEmpty(_.trim(name))) {
      return 'Please provide a valid name';
    }

    if (_.size(name) > 200) {
      return 'Folder name cannot be greater than 200 characters';
    }

    return null;
  }

  const handleConfirm = () => {
    const e = validateForm();
    if (e) {
      setError(e);
      return
    }

    collectionContext.createSection(collection, name.trim());
    onFinish(0);
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleConfirm();
  }

  return (
    <Slider.Dialog width="300" className={styles.addSectionDialog} show={show} onClose={onClose}>
      <Slider.Header
        title={<>creating <br />new section...</>}
      >
      </Slider.Header>
      <Slider.Body>
        <form className={styles.addSectionForm} onSubmit={handleSubmit}>
          <Input autoFocus className={styles.input} value={name} placeholder="Add a section name..." onChange={handleChange} />
          <Reveal show={!!error}>
            <div className={styles.error}>
              <i className="fa-solid fa-triangle-exclamation"></i>
              {error}
            </div>
          </Reveal>
        </form>
        <p>Creating sections is perfect when you have more than ten pieces to share. Add more as your content grows—let's get started</p>
      </Slider.Body>
      <Slider.Actions>
        <Button icon="paperflite-tick"
          text={'Create'}
          variant='primary'
          disabled={!name}
          onClick={handleConfirm}
        />
        <Button text="Cancel"
          variant='secondary'
          onClick={handleCancel}
        ></Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
}

export { CollectionContent, AddSectionDialog };